/**
 * Signed-in customer identity, read from the `ep_appinfo` hydration.
 *
 * `customer.id` is three-valued and every consumer has to handle all three:
 *
 *   - a number   — signed in
 *   - `false`    — known signed out (content-for-footer.liquid's default)
 *   - `undefined`— unknown: app.init() hasn't hydrated app.properties yet,
 *                  or it threw and never will
 *
 * The third case is the one that bites. `appGet` yields `undefined` until
 * hydration, and `undefined !== false` reads as "signed in" — which is how
 * the waitlist came to take its logged-in branch with no email to register,
 * then fail silently. Encoding those rules once, here, keeps the next reader
 * of `customer.id` from re-deriving them (and getting it wrong the same way).
 *
 * This deliberately does not live in `app-get.esm.js`: that module is a
 * generic path reader, and customer semantics on it would be a special case
 * bolted onto shared infrastructure.
 *
 * Source path: theme/js/core/customer.esm.js
 * Built name : ./customer.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-09-08
 * Updated: 2026-09-28 - loginUrlReturningTo() for new customer accounts
 *
 * [Customer]
 */

import { appGet } from './app-get.js';

/** The signed-in customer id, `false` when signed out, `undefined` when unknown. */
export function customerId() {
  return appGet('customer.id');
}

/** True only when a customer id is positively established. Fails closed. */
export function isSignedIn() {
  const id = customerId();
  return id !== false && id != null;
}

/**
 * A login URL that brings the shopper back to `returnTo` (a same-origin path)
 * after they sign in.
 *
 * The two account systems take different routes and different parameters:
 *
 *   - New customer accounts: `routes.account_login_url` is
 *     `/customer_authentication/redirect?…`. `/account` and `/account/login`
 *     both bounce to Shopify's hosted account pages (shopify.com/<id>/account)
 *     and never come back; `/customer_authentication/login?return_to=` runs
 *     the OAuth login and returns to the storefront path.
 *   - Classic accounts: `/account/login?return_url=` — the login form carries
 *     `return_url` through as a hidden field and redirects there on success.
 *
 * Needs a full page load (`location.assign`), not a Turbo visit: the login
 * redirects off-origin.
 */
export function loginUrlReturningTo(returnTo) {
  const param = encodeURIComponent(returnTo || '/');
  const routeLogin = appGet('customer.loginUrl') || '';
  return routeLogin.indexOf('/customer_authentication/') !== -1
    ? '/customer_authentication/login?return_to=' + param
    : '/account/login?return_url=' + param;
}
